import { useAuth } from "app/modules/auth";
import React, { useEffect, useState } from "react";
import db from "services/firebase";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { Link } from "react-router-dom";

interface FrameworksCardProps {}

const FrameworksCard: React.FC<FrameworksCardProps> = () => {
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([]);
  const { currentUser } = useAuth();
  const defaultFrameworks: string[] = ["ISO 27001", "SOC 2", "GDPR"];
  const [controls, setControls] = useState<any[]>([]);
  const [totalISO, setTotalISO] = useState(0);
  const [totalSOC, setTotalSOC] = useState(0);
  const [healthyISOControlsCount, setHealthyISOControlsCount] = useState(0);
  const [healthySOCControlsCount, setHealthySOCControlsCount] = useState(0);
  const frameworksToDisplay: string[] = [];
  const [totalGDPR, setTotalGDPR] = useState(0);
  const [healthyGDPRControlsCount, setHealthyGDPRControlsCount] = useState(0);
  const [totalHIPAA, setTotalHIPAA] = useState(0);
  const [healthyHIPAAControlsCount, setHealthyHIPAAControlsCount] = useState(0);

  useEffect(() => {
    if (!currentUser?.tenantId) {
      return
    }
    getActiveFramworks();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.tenantId) {
      return
    }
    getControls();
  }, [activeFrameworks, currentUser]);

  const getActiveFramworks = () => {
    try {
      const tenantId = currentUser?.tenantId || "";
      if (!tenantId) return;
      
      const tenantRef = doc(db, "tenants", tenantId);

      const testsCollectionRef = collection(tenantRef, "active_frameworks");

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }));
        const frameworks = frameworksData[0]?.frameworks || [];
        setActiveFrameworks(frameworks);
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error getting tests:", error);
    }
  };

  const getControls = () => {
    try {
      const tenantId = currentUser?.tenantId || "";
      if (!tenantId) return;
      
      const tenantRef = doc(db, "tenants", tenantId);
      const testsCollectionRef = collection(tenantRef, "controls");

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const controlsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          framework: doc.data().control_framework_mappings,
          status: doc.data().control_evidence_status,
        }));

        const filteredControls = controlsData.filter((control) => {
          if (!control.framework) return false;

          return activeFrameworks.some(
            (framework) =>
              control.framework.startsWith(framework) &&
              !control.framework.startsWith("ISO 27001:2013")
          );
        });

        setControls(filteredControls);
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error getting controls:", error);
    }
  };

  useEffect(() => {
    const iso27001Controls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings;
      return framework && framework.includes("ISO");
    });

    // Count the number of documents that start with "ISO 27001"
    const iso27001ControlsCount = iso27001Controls.length;
    setTotalISO(iso27001ControlsCount);

    const SOC2Controls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings;
      return framework && framework.startsWith("SOC");
    });

    const SOC2ControlsCount = SOC2Controls.length;
    setTotalSOC(SOC2ControlsCount);

    const GDPRControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings;
      return framework && framework.includes("GDPR");
    });
    setTotalGDPR(GDPRControls.length);

    const HIPAAControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings;
      return framework && framework.includes("HIPAA");
    });
    setTotalHIPAA(HIPAAControls.length);

  }, [controls]);

  useEffect(() => {
    if (!controls || controls.length === 0) {
      return;
    }

    // Filter documents with framework containing "ISO" and status "Ok"
    const isoHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || "";
      const status = doc.control_evidence_status || "";
      return framework.includes("ISO") && status.toLowerCase() === "ok";
    });

    // Count the number of documents with framework containing "ISO" and status "Ok"
    const isoHealthyControlsCount = isoHealthyControls.length;

    // Update state with the count of "ISO" controls with status "Ok"
    setHealthyISOControlsCount(isoHealthyControlsCount);

    // Filter documents with framework containing "SOC" and status "OK"
    const socHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || "";
      const status = doc.control_evidence_status || "";
      return framework.includes("SOC") && status.toLowerCase() === "ok";
    });

    // Count the number of documents with framework containing "SOC" and status "OK"
    const socHealthyControlsCount = socHealthyControls.length;

    // Update state with the count of "SOC" controls with status "OK"
    setHealthySOCControlsCount(socHealthyControlsCount);

    const gdprHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || "";
      const status = doc.control_evidence_status || "";
      return framework.includes("GDPR") && status.toLowerCase() === "ok";
    });
    setHealthyGDPRControlsCount(gdprHealthyControls.length);

    const HIPAAHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || "";
      const status = doc.control_evidence_status || "";
      return framework.includes("HIPAA") && status.toLowerCase() === "ok";
    });
    setHealthyHIPAAControlsCount(HIPAAHealthyControls.length);
  }, [controls]);

  // Add all active frameworks first
  activeFrameworks.forEach((framework) => {
    frameworksToDisplay.push(framework);
  });

  // Fill remaining slots with default frameworks that aren't already active
  let remainingSlots = 3 - frameworksToDisplay.length;
  if (remainingSlots > 0) {
    defaultFrameworks.forEach((framework) => {
      // Check if this default framework family is already included in active frameworks
      const frameworkFamily = framework.split(":")[0]; // Get the framework name without version
      const isAlreadyActive = activeFrameworks.some((activeFramework) =>
        activeFramework.includes(frameworkFamily)
      );

      // Only add default if it's not already in active frameworks and we need more
      if (remainingSlots > 0 && !isAlreadyActive) {
        frameworksToDisplay.push(framework);
        remainingSlots--;
      }
    });
  }

  // Limit to 3 frameworks max
  const displayFrameworks = frameworksToDisplay.slice(0, 3);

  // Map icons to framework types
  const getIconForFramework = (framework: string) => {
    if (framework.includes("ISO")) {
      return (
        <img
          src="/media/framework/ISO27001.svg"
          alt="ISO 27001"
          width={18}
          height={18}
          className="icon mb-1"
        />
      );
    } else if (framework.includes("SOC")) {
      return (
        <img
          src="/media/framework/SOC2.svg"
          alt="SOC 2"
          width={18}
          height={18}
          className="icon mb-1"
        />
      );
    } else if (framework.includes("GDPR")) {
      return (
        <img
          src="/media/framework/GDPR.svg"
          alt="GDPR"
          width={18}
          height={18}
          className="icon mb-1"
        />
      );
    } else if (framework.includes("HIPAA")) {
      return (
        <img
          src="/media/framework/HIPPA.png"
          alt="HIPAA"
          width={18}
          height={18}
          className="icon mb-1"
        />
      );
    } else {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#EE3043"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle mb-1"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M12 9v4" />
          <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
          <path d="M12 16h.01" />
        </svg>
      );
    }
  };

  const getMetricForPosition = (index: number) => {
    const framework = displayFrameworks[index];

    if (!framework) return 0;

    if (framework.includes("ISO")) {
      return totalISO !== 0 ? (
        `${((healthyISOControlsCount / totalISO) * 100).toFixed(0)}%`
      ) : (
        <span className="fs-6 fw-semibold" style={{ color: "#5A5A5A" }}>
          0%
        </span>
      );
    } else if (framework.includes("SOC")) {
      return totalSOC !== 0 ? (
        `${((healthySOCControlsCount / totalSOC) * 100).toFixed(0)}%`
      ) : (
        <span className="fs-6 fw-semibold" style={{ color: "#5A5A5A" }}>
          0%
        </span>
      );
    }else if (framework.includes("GDPR")) {
      return totalGDPR !== 0 ? (
        `${((healthyGDPRControlsCount / totalGDPR) * 100).toFixed(0)}%`
      ) : (
        <span className="fs-6 fw-semibold" style={{ color: "#5A5A5A" }}>
          0%
        </span>
      );
    }else if (framework.includes("HIPAA")) {
      return totalHIPAA !== 0 ? (
        `${((healthyHIPAAControlsCount / totalHIPAA) * 100).toFixed(0)}%`
      ) : (
        <span className="fs-6 fw-semibold" style={{ color: "#5A5A5A" }}>
          0%
        </span>
      );
    } else if (index === 1) {
      return (
        <span className="fs-6 fw-semibold text-muted">
          0%
        </span>
      );
    } else if (index === 2) {
      return (
        <span className="fs-6 fw-semibold text-muted">
          0%
        </span>
      );
    }

    return (
      <span className="fs-6 fw-semibold" style={{ color: "#5A5A5A" }}>
        0%
      </span>
    );
  };

  return (
    <div className="col-sm-4">
      <div className="card card-flush h-lg-80" style={{ padding: "24px" }}>
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-4 text-dark">Frameworks</span>
        </h3>
        <div className="pt-0">
          {displayFrameworks.map((framework, index) => (
            <div
              className={`d-flex flex-stack ${index === 0 ? "mt-5" : "mt-4"}`}
              key={index}
            >
              <div className="text-gray-700 fw-semibold fs-6 me-2">
                <div className="d-inline-block me-2 align-items-center">
                  {getIconForFramework(framework)}
                </div>
                <span className={activeFrameworks.includes(framework) ? "" : "text-muted"}>
                {framework}
              </span>
              </div>
              <div className="d-flex align-items-senter">
                <span className="text-gray-900 fw-bold">
                  {getMetricForPosition(index)}
                </span>
              </div>
            </div>
          ))}

          <div className="d-flex justify-content-between align-items-center mt-4">
            <div
              className="d-flex align-items-center hover-group"
              style={{ cursor: "pointer" }}
            >
              <Link
                to="/frameworks"
                className="text-dark fw-medium text-decoration-underline hover-text hover-text-decoration-underline me-1"
              >
                View frameworks
              </Link>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-right transition-transform hover-effect"
                style={{ transition: "transform 0.2s ease-in-out" }}
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M9 6l6 6l-6 6" />
              </svg>
              <style>
                {`
                  .hover-group:hover .hover-effect {
                    transform: translateX(3px);
                  }
                  .hover-group:hover .hover-text {
                    color: #6c757d !important;
                  }
                  .hover-group:hover svg {
                    stroke: #6c757d;
                  }
                `}
              </style>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrameworksCard;
